import type { AuthMode, DomainEvent } from "@szyos/contracts";
import type { StepView } from "@szyos/domain";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { CostRow } from "../api";
import { AUTH_MODE_LABEL, adapterLabel, describe, time, usd, type TimelineCategory } from "../format";
import { Icon } from "../icons";

export function Stepper({ steps }: { steps: StepView[] }) {
  return (
    <ol className="stepper" aria-label="Phasen des Auftrags">
      {steps.map((s, i) => {
        const cls = [
          "step",
          s.status === "done" && "is-done",
          s.status === "current" && "is-current",
          s.gate && "is-gate",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <li key={s.key} className={cls} aria-current={s.status === "current" ? "step" : undefined}>
            <span className="step-dot">{s.status === "done" ? <Icon name="check" /> : i + 1}</span>
            <span>{s.label}</span>
            <span className="sr-only">
              ,{" "}
              {s.status === "done"
                ? "abgeschlossen"
                : s.status === "current"
                  ? s.gate
                    ? "aktuell, Entscheidung nötig"
                    : "aktuell"
                  : "ausstehend"}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

const FILTERS: { id: TimelineCategory | "all"; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "plan", label: "Planung" },
  { id: "build", label: "Umsetzung" },
  { id: "test", label: "Tests" },
  { id: "release", label: "Auslieferung" },
];

/** Live-Timeline aus dem Event-Log; nur bedeutsame Ereignisse. */
export function Timeline({ events }: { events: DomainEvent[] }) {
  const [filter, setFilter] = useState<TimelineCategory | "all">("all");
  const items = events
    .map((e) => ({ e, d: describe(e) }))
    .filter((x): x is { e: DomainEvent; d: NonNullable<ReturnType<typeof describe>> } =>
      Boolean(x.d?.important),
    )
    .slice(-12);
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    listRef.current?.scrollTo({ left: listRef.current.scrollWidth, behavior: "smooth" });
  }, [items.length]);
  const lastIndex = items.length - 1;
  return (
    <div className="panel timeline-panel">
      <div className="panel-head">
        <div>
          <h2>Live Timeline</h2>
          <p className="muted">Belegt aus dem Event-Log</p>
        </div>
        <div className="filters" role="group" aria-label="Timeline filtern">
          {FILTERS.map((f) => {
            const count = f.id === "all" ? items.length : items.filter((x) => x.d.category === f.id).length;
            return (
              <button
                key={f.id}
                type="button"
                className="filter"
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label} ({count})
              </button>
            );
          })}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="muted">Noch keine Ereignisse.</p>
      ) : (
        <ol
          className="timeline"
          ref={listRef}
          tabIndex={0}
          aria-label="Ereignisse des Auftrags, horizontal scrollbar"
        >
          {items.map(({ e, d }, i) => {
            const next = items[i + 1]?.d;
            const muted = filter !== "all" && d.category !== filter;
            const gate = i === lastIndex && d.tone === "amber";
            return (
              <li
                key={e.eventId}
                className={["tl-item", gate && "is-gate", muted && "is-muted"].filter(Boolean).join(" ")}
                style={{
                  ["--c" as string]: `var(--${d.tone})`,
                  ["--c2" as string]: `var(--${next?.tone ?? d.tone})`,
                }}
              >
                <span className="tl-tick" />
                <span className="tl-dot" />
                <time className="tl-time" dateTime={e.occurredAt}>
                  {time(e.occurredAt)}
                </time>
                <span className="tl-text">{d.text}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Agenten-Aktivität: Rolle vor Provider, neueste oben. */
export function Activity({
  events,
  title = "Agenten-Aktivität",
  limit = 4,
}: {
  events: DomainEvent[];
  title?: string;
  limit?: number;
}) {
  const items = events
    .filter(
      (e) =>
        e.eventType.startsWith("run.") ||
        e.eventType.startsWith("ci.") ||
        e.eventType === "workitem.verified" ||
        e.eventType === "provider_auth.changed",
    )
    .map((e) => ({ e, d: describe(e) }))
    .filter((x): x is { e: DomainEvent; d: NonNullable<ReturnType<typeof describe>> } => Boolean(x.d))
    .slice(-limit)
    .reverse();
  return (
    <div className="panel activity-panel">
      <div className="panel-head">
        <h2>{title}</h2>
        <LiveBadge />
      </div>
      {items.length === 0 ? (
        <p className="muted">Noch keine Aktivität.</p>
      ) : (
        <ul className="activity" aria-live="polite">
          {items.map(({ e, d }) => (
            <li key={e.eventId} className="act" style={{ ["--c" as string]: `var(--${d.tone})` }}>
              <span className="act-icon">
                <Icon name={d.icon} />
              </span>
              <div>
                <strong>
                  {d.who}
                  {typeof e.payload.adapter === "string" && (
                    <span className="provider">
                      {adapterLabel(e.payload.adapter)}
                      {AUTH_MODE_LABEL[e.payload.authMode as AuthMode]
                        ? ` · ${AUTH_MODE_LABEL[e.payload.authMode as AuthMode]}`
                        : ""}
                    </span>
                  )}
                </strong>
                <span>{d.text}</span>
              </div>
              <time dateTime={e.occurredAt}>{time(e.occurredAt)}</time>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LiveBadge() {
  return (
    <span className="live">
      <span className="pulse-dot" aria-hidden="true" />
      Live
    </span>
  );
}

const COST_TONES = ["lime", "cyan", "violet", "coral", "orange"] as const;

/** Zeile des Kostenrings; `authMode: "subscription"` steht für Abo-Läufe ohne Dollarbetrag. */
export type CostRingRow = CostRow;

/**
 * Kostenring: gemessen und geschätzt getrennt, Abo-Kontingente separat (docs/03 §8, ACC-36).
 * Abo-Läufe zählen nie in die Dollarsumme und zeigen keinen Betrag.
 */
export function CostRing({
  rows,
  budgetUsd,
  title = "Ressourcen & Kosten",
}: {
  rows: CostRingRow[];
  budgetUsd: number | null;
  title?: string;
}) {
  const paid = rows.filter((row) => row.authMode !== "subscription");
  const total = paid.reduce((s, r) => s + r.measuredUsd + r.estimatedUsd, 0);
  const r = 52;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const label = (row: CostRingRow) =>
    `${adapterLabel(row.adapter)}${row.authMode && AUTH_MODE_LABEL[row.authMode] ? ` · ${AUTH_MODE_LABEL[row.authMode]}` : ""}`;
  return (
    <div className="panel cost-panel">
      <div className="panel-head">
        <h2>{title}</h2>
        <span className="select">Heute</span>
      </div>
      <div className="cost-body">
        <div
          className="donut"
          role="img"
          aria-label={`Kosten ${usd(total)}${budgetUsd ? ` von ${usd(budgetUsd)} Budget` : ""}`}
        >
          <svg viewBox="0 0 120 120" aria-hidden="true">
            <circle className="track" cx={60} cy={60} r={r} />
            {total > 0 &&
              paid.map((row, i) => {
                const len = ((row.measuredUsd + row.estimatedUsd) / total) * circ;
                const vis = Math.max(1, len - (paid.length > 1 ? 5 : 0));
                const el = (
                  <circle
                    key={`${row.adapter}-${row.authMode ?? ""}`}
                    className="seg"
                    cx={60}
                    cy={60}
                    r={r}
                    style={{ ["--c" as string]: `var(--${COST_TONES[i % COST_TONES.length]})` }}
                    strokeDasharray={`${vis} ${circ - vis}`}
                    strokeDashoffset={-offset}
                  />
                );
                offset += len;
                return el;
              })}
          </svg>
          <div className="donut-center">
            <strong>{usd(total)}</strong>
            <span>{budgetUsd ? `von ${usd(budgetUsd)}` : "heute"}</span>
          </div>
        </div>
        <ul className="cost-list">
          {rows.length === 0 && <li className="muted">Noch keine Läufe heute.</li>}
          {paid.map((row, i) => (
            <li
              key={`${row.adapter}-${row.authMode ?? ""}`}
              style={{ ["--c" as string]: `var(--${COST_TONES[i % COST_TONES.length]})` }}
            >
              <i />
              <span>{label(row)}</span>
              <span className="pct">
                {total > 0 ? Math.round(((row.measuredUsd + row.estimatedUsd) / total) * 100) : 0} %
              </span>
              <span className="amt" title={row.estimatedUsd > 0 ? "teilweise geschätzt" : "gemessen"}>
                {row.estimatedUsd > 0 && <span className="est">≈ </span>}
                {usd(row.measuredUsd + row.estimatedUsd)}
              </span>
            </li>
          ))}
          {rows
            .filter((row) => row.authMode === "subscription")
            .map((row) => (
              <li key={`${row.adapter}-subscription`} className="cost-quota">
                <i />
                <span>
                  {label(row)}
                  <small className="block">
                    {row.runs} {row.runs === 1 ? "Lauf" : "Läufe"} im Abo-Kontingent, kein Dollarbetrag
                  </small>
                  {(row.apiEquivalentUsd ?? 0) > 0 && (
                    <small className="block" title="Schätzung des Anbieters, zählt nie auf Budget oder Summe">
                      ≈ API-Gegenwert {usd(row.apiEquivalentUsd ?? 0)}
                    </small>
                  )}
                </span>
              </li>
            ))}
        </ul>
      </div>
      <p className="cost-note">
        <Icon name="lock" /> Gemessen, ≈ geschätzt. Abo-Kontingente separat, ohne Dollarbetrag in der Summe.
      </p>
    </div>
  );
}

export function Drawer({
  title,
  sub,
  icon,
  onClose,
  children,
}: {
  title: string;
  sub: string;
  icon: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    opener.current = document.activeElement;
    ref.current?.querySelector<HTMLButtonElement>(".icon-btn")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && ref.current) {
        const f = ref.current.querySelectorAll<HTMLElement>(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
        );
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-labelledby="drawerTitle" ref={ref}>
        <div className="drawer-inner">
          <div className="drawer-head">
            <span className="node-icon">
              <Icon name={icon} />
            </span>
            <div>
              <h2 id="drawerTitle">{title}</h2>
              <p>{sub}</p>
            </div>
            <button type="button" className="icon-btn" aria-label="Details schließen" onClick={onClose}>
              <Icon name="x" />
            </button>
          </div>
          {children}
        </div>
      </aside>
    </>
  );
}
